import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadBookingSummary } from "./clientSession";
import googleCalendarWordmark from "./assets/google-calendar-wordmark-174.svg";
import { formatTimezoneLabel } from "./gcalUiHelpers";
import { useGcalOfficialStyles } from "./useGcalOfficialStyles";

const EVENT_TITLE = "30 Minute Phone Call";
const SLOT_MINUTES = 30;

function parseDateKey(key) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function formatConfirmClock(mins) {
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h24 >= 12 ? "pm" : "am";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const clock = m === 0 ? `${h12}:00` : `${h12}:${String(m).padStart(2, "0")}`;
  return { clock, period };
}

function formatConfirmTimeRange(startMinutes) {
  const start = formatConfirmClock(startMinutes);
  const end = formatConfirmClock(startMinutes + SLOT_MINUTES);
  if (start.period === end.period) {
    return `${start.clock} – ${end.clock}${end.period}`;
  }
  return `${start.clock}${start.period} – ${end.clock}${end.period}`;
}

function formatConfirmWhenLine(dateKey, startMinutes) {
  const date = parseDateKey(dateKey);
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  return `${weekday} · ${formatConfirmTimeRange(startMinutes)}`;
}

function stripTimezoneFromTimeLabel(timeLabel) {
  if (!timeLabel) return "";
  return timeLabel.replace(/\s*\([^)]*\)\s*$/, "").trim();
}

function bookingEventDate(booking) {
  if (!booking) return null;
  if (booking.dateKey) return parseDateKey(booking.dateKey);
  if (booking.dateLabel) {
    const parsed = new Date(booking.dateLabel);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
}

export default function Confirmation() {
  useGcalOfficialStyles();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const [booking, setBooking] = useState(null);
  const [timezoneLabel, setTimezoneLabel] = useState(() =>
    formatTimezoneLabel(Intl.DateTimeFormat().resolvedOptions().timeZone),
  );
  const loginEmail = localStorage.getItem("userEmail") || "";

  useEffect(() => {
    const sessionId = localStorage.getItem("session_id");
    if (!sessionId) {
      navigate("/");
      return;
    }

    const load = async () => {
      const cached = loadBookingSummary(sessionId);
      if (cached) {
        setBooking(cached);
        return;
      }

      if (!API_URL) return;
      try {
        const res = await fetch(`${API_URL}/api/get-booking?session_id=${encodeURIComponent(sessionId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.booking) {
          setBooking({
            eventTitle: EVENT_TITLE,
            dateLabel: data.booking.date,
            timeLabel: data.booking.time,
            email: data.booking.email,
          });
        }
      } catch {
        /* ignore */
      } finally {
      }
    };

    load();
  }, [navigate, API_URL]);

  useEffect(() => {
    if (booking?.timezone) {
      setTimezoneLabel(booking.timezone);
      return;
    }
    if (booking?.timezoneIana) {
      setTimezoneLabel(formatTimezoneLabel(booking.timezoneIana));
    }
  }, [booking?.timezone, booking?.timezoneIana]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://ipwho.is/");
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.success) return;
        const tz = data.timezone?.id;
        if (tz) setTimezoneLabel(formatTimezoneLabel(tz));
      } catch {
        /* keep booking or browser default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.title = "30 Minute Phone Call";
    document.documentElement.classList.add("gcal-appointments-page");
    document.body.classList.add("xPxted", "EIlDfe", "gcal-confirmed-body");
    return () => {
      document.documentElement.classList.remove("gcal-appointments-page");
      document.body.classList.remove("xPxted", "EIlDfe", "gcal-confirmed-body");
    };
  }, []);

  const displayEmail = loginEmail || booking?.email || "";

  const dateBox = useMemo(() => {
    const d = bookingEventDate(booking);
    if (!d) return null;
    return {
      day: d.getDate(),
      month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    };
  }, [booking]);

  const whenLine = useMemo(() => {
    if (!booking) return "";
    if (booking.dateKey && booking.startMinutes != null) {
      return formatConfirmWhenLine(booking.dateKey, booking.startMinutes);
    }
    const eventDate = bookingEventDate(booking);
    const timeOnly = stripTimezoneFromTimeLabel(booking.timeLabel);
    if (eventDate && timeOnly) {
      const weekday = eventDate.toLocaleDateString("en-US", { weekday: "long" });
      return `${weekday} · ${timeOnly}`;
    }
    if (booking.dateLabel && timeOnly) {
      return `${booking.dateLabel} · ${timeOnly}`;
    }
    return timeOnly;
  }, [booking]);

  return (
    <div className="gcal-confirmed-page">
      <div className="gcal-confirmed-shell">
        <div className="t21zO gcal-confirmed-dialog" role="main">
          <div hidden id="c378">
            Booking confirmed
          </div>

          <div className="gcal-confirmed-head">
            <div className="lCKVYd gcal-confirmed-check" aria-hidden="true">
              <svg focusable="false" width="28" height="28" viewBox="0 0 24 24" className="NMm5M">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
              </svg>
            </div>
            <h1 className="eLar5c">Booking confirmed</h1>
            <div className="zPEEvc RZsspe" role="presentation">
              <div className="gcal-confirmed-email-lead">
                Email will be sent within 24-48 hours to
              </div>
              {displayEmail ? <div className="bkcSEd">{displayEmail}</div> : null}
            </div>
          </div>

          {booking && (
            <>
              <hr className="VnX9cb" />
              <div className="TnFfie gcal-confirmed-event">
                {dateBox && (
                  <div className="xFPIWe">
                    <div className="ADJDrd">{dateBox.day}</div>
                    <div className="JjB5y">{dateBox.month}</div>
                  </div>
                )}
                <div className="FVezU">
                  <div className="b46Tld AP1Rif">{booking.eventTitle || EVENT_TITLE}</div>
                  {whenLine ? <div className="TFHaFb">{whenLine}</div> : null}
                  <div className="TFHaFb" role="presentation">
                    {timezoneLabel}
                  </div>
                </div>
              </div>

              <hr className="VnX9cb" />
              <div className="gcal-confirmed-change">
                <div className="jrbd6d" role="presentation">
                  Need to change?
                  <br />
                  <span className="fVXLmf fVXLmf--static">Cancel your appointment</span>
                </div>
              </div>
            </>
          )}

          <div className="NMgyaf yswnI gcal-confirmed-promo">
            <img src={googleCalendarWordmark} alt="Google Calendar" className="gcal-confirmed-wordmark" />
            <div className="gcal-confirmed-promo-text">Create your own appointment page</div>
            <span className="vrYdS bQPbRb">Learn More</span>
          </div>
        </div>
      </div>
    </div>
  );
}
